import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import _ from "lodash";
import cx from "classnames";

import {
  resetPassword,
  resetPasswordRequest,
  resetPasswordLink,
} from "actions/users";

import styles from "./password.module.scss";
import { Button, Container, Grid, Icon, Input } from "semantic-ui-react";
import SaveChanges from "components/ui/Buttons/SaveButton";
import Cancel from "components/ui/Buttons/CancelButton";
import {
  PasswordValidator,
  RenderPasswordError,
} from "components/ui/Validator/Password/PasswordValidator";
import { isAccountSuspended } from "components/features/Util/Util";

export default function Password({ user, permissionData }) {
  const dispatch = useDispatch();

  const [id, setId] = useState(null);
  const [initialRendering, setInitialRender] = useState(true);

  const [password, setPassword] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [isPasswordSubmitted, setPasswordSubmitted] = useState(false);

  const [copied, setCopied] = useState(false);
  const [resetPasswordURL, setResetPasswordURL] = useState(null);

  const [openReset, setOpenReset] = useState(false);
  const [openChange, setOpenChange] = useState(false);

  const account = useSelector((state) => state.accounts.account);
  const resettingPassword = useSelector((state) =>
    _.get(state, "users.resettingPassword")
  );

  const resettingPasswordRequest = useSelector((state) =>
    _.get(state, "users.resettingPasswordRequest")
  );

  const resettingPasswordUrl = useSelector((state) =>
    _.get(state, "users.resettingPasswordUrl")
  );

  useEffect(() => {
    if (user) {
      setId(user.id);
    }
  }, [user]);

  useEffect(() => {
    setPasswordError(PasswordValidator(password));
  }, [password]);

  useEffect(() => {
    if (!initialRendering && resettingPassword) {
      if (!resettingPassword.resetting && resettingPassword.success) {
        toast.success(`Changed ${user.firstName} password successfully`);
        setPassword("");
        setPasswordError("");
        setPasswordSubmitted(false);
      }
    }
  }, [resettingPassword]);

  useEffect(() => {
    if (!initialRendering && resettingPasswordRequest) {
      if (
        !resettingPasswordRequest.requesting &&
        resettingPasswordRequest.success
      ) {
        toast.success(`Requested ${user.firstName} to reset password`);
      }
    }
  }, [resettingPasswordRequest]);

  useEffect(() => {
    if (!initialRendering && resettingPasswordUrl) {
      if (!resettingPasswordUrl.generating && resettingPasswordUrl.success) {
        setResetPasswordURL(resettingPasswordUrl.url);
        toast.success(`Generated reset password link for ${user.firstName}`);
      }
    }
  }, [resettingPasswordUrl]);

  setTimeout(() => {
    setInitialRender(false);
  }, [3000]);

  const onSetOpenReset = () => {
    setOpenReset(!openReset);
  };

  const onSetOpenChange = () => {
    setOpenChange(!openChange);
  };

  const onResetPassword = () => {
    setPasswordSubmitted(true);

    if (password && !passwordError) {
      console.log("id: ", id);
      console.log("password: ", password);

      dispatch(resetPassword({ id, password }));
    }
  };

  const onCancelPassword = () => {
    setPassword("");
    setPasswordSubmitted(false);
    setOpenReset(false);
  };

  const onSendResetPassword = () => {
    dispatch(resetPasswordRequest({ id }));
  };

  const onGetRestPassword = () => {
    dispatch(resetPasswordLink({ id }));
  };

  return (
    <>
      <Container className={cx(styles.password)}>
        <Grid>
          <Grid.Row className={cx(styles.title)}>
            <Grid.Column>Password Settings</Grid.Column>
          </Grid.Row>
        </Grid>

        <Grid className={cx(styles.section)}>
          <Grid.Row
            columns={2}
            onClick={onSetOpenReset}
            className={cx(styles.title)}
          >
            <Grid.Column width={12}>Reset password</Grid.Column>
            <Grid.Column width={4} textAlign="right">
              <span className={cx(styles.icon)}>
                {openReset ? <Icon name="minus" /> : <Icon name="plus" />}
              </span>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row className={cx(styles.text)}>
            <Grid.Column>
              Create a new password for the selected user.
            </Grid.Column>
          </Grid.Row>

          {openReset && (
            <>
              <Grid.Row className={cx(styles.inputSet)}>
                <Grid.Column>
                  <Input
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    className={cx(styles.input)}
                    type="password"
                    disabled={isAccountSuspended(account)}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row columns={2} className={cx(styles.btnSet)}>
                <Grid.Column width={8}>
                  {isPasswordSubmitted && (
                    <> {RenderPasswordError(passwordError)}</>
                  )}
                </Grid.Column>
                <Grid.Column width={8} textAlign="right">
                  <SaveChanges
                    content="Reset"
                    onClick={onResetPassword}
                    disabled={isAccountSuspended(account) || !_.get(permissionData, 'user.write', false)}
                  />
                  <Cancel
                    content="Cancel"
                    onClick={onCancelPassword}
                    disabled={isAccountSuspended(account) || !_.get(permissionData, 'user.write', false)}
                  />
                </Grid.Column>
              </Grid.Row>
            </>
          )}
        </Grid>

        <div className={cx(styles.bl)}></div>

        <Grid className={cx(styles.section)}>
          <Grid.Row
            columns={2}
            onClick={onSetOpenChange}
            className={cx(styles.title)}
          >
            <Grid.Column width={12}>Change password via email</Grid.Column>
            <Grid.Column width={4} textAlign="right">
              <span className={cx(styles.icon)}>
                {openChange ? <Icon name="minus" /> : <Icon name="plus" />}
              </span>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row className={cx(styles.text)}>
            <Grid.Column>
              Send a password reset option to the user.
              Choose how you’d like to deliver it:
            </Grid.Column>
          </Grid.Row>

          {openChange && (
            <>
              <Grid.Row className={cx(styles.iconset)}>
                <Grid.Column className={styles.mailCol} textAlign="right">
                  <Button
                    className={cx(styles.text)}
                    onClick={onSendResetPassword}
                    content={
                      <span>
                        <Icon
                          circular
                          name="envelope outline"
                          className={cx(styles.icon)}
                        />
                        Send a mail
                      </span>
                    }
                    disabled={isAccountSuspended(account) || !_.get(permissionData, 'user.write', false)}
                  />
                </Grid.Column>
                <Grid.Column className={styles.mailCol} textAlign="left">
                  <Button
                    className={cx(styles.text)}
                    onClick={onGetRestPassword}
                    content={
                      <span>
                        <Icon
                          circular
                          name="linkify"
                          className={cx(styles.icon)}
                        />
                        Get a link
                      </span>
                    }
                    disabled={isAccountSuspended(account) || !_.get(permissionData, 'user.write', false)}
                  />
                </Grid.Column>
              </Grid.Row>

              {resetPasswordURL && (
                <Grid.Row columns={2} className={cx(styles.resetUrl)}>
                  <Grid.Column width={10}>
                    <span className={cx(styles.text)}>{resetPasswordURL}</span>
                  </Grid.Column>
                  <Grid.Column width={6} textAlign="right" className={styles.copyPassword}>
                    <CopyToClipboard
                      text={resetPasswordURL}
                      onCopy={() => setCopied(true)}
                    >
                      <span className={cx(styles.copy)}>
                        {copied ? "copied" : "copy"}{" "}
                      </span>
                    </CopyToClipboard>
                  </Grid.Column>
                </Grid.Row>
              )}
            </>
          )}
        </Grid>
      </Container>
    </>
  );
}
